// Internal-staff capability model — the single frontend source of truth for
// "who can do what" inside the internal dashboard (and, over time, the rest of
// the app). Replaces person-as-permission checks like
// `checkUserAccess(uid, 'CASEY')` with capability checks like
// `can(uid, 'events:edit')`.
//
// STORAGE: this is the "config file first" step. Grants live here in code
// (still a deploy to change). The public API below — `can()` / `capabilitiesFor()`
// — is deliberately storage-agnostic so a later migration to a DB table +
// Team & Access admin UI can swap the internals without touching call sites.
//
// NOTE: this is UX/authoring-side only. It shapes what the UI offers. It is NOT
// a security boundary — every sensitive action must ALSO be enforced on the
// backend (mirror this model in showman-db-v1 with a `requireCapability`
// middleware). Hiding a nav item does not stop the API call.

import {
  JOSH, JASON, LAYNE, CASEY, RACHEL, OLIVIA, BRANDY, SUSAN, RUTH, PEYTON, KELSEY
} from '@/userAccessLevel'

// Capability constants — reference these, never raw strings, to avoid typos.
export const CAP = {
  EVENTS_EDIT: 'events:edit',
  SPECIES_MANAGE: 'species:manage',
  SPECIALISTS_MANAGE: 'specialists:manage',
  WORKLIST_PUBLISH: 'worklist:publish-queue',
  WORKLIST_SERIES: 'worklist:series-points',
  IMPERSONATION_USE: 'impersonation:use',
  EXHIBITORS_MERGE: 'exhibitors:merge',
  BULK_EMAIL_SEND: 'bulk-email:send',
  // Series → show nomination import: writes entries into another organiser's
  // event, so it is a named grant, not part of any role bundle. Mirrored on the
  // backend by SERIES_IMPORT_UIDS in showman-db-v1/src/utils/internalSupport.js.
  SERIES_IMPORT: 'series:import',
  // Destructive — kept separate from events:edit on purpose
  EVENTS_CANCEL_DELETE: 'events:cancel-delete',
  // Payouts and closeout. Enforced on the backend by requireCapability in
  // showman-db-v1/src/utils/capabilities.js, which mirrors these grants.
  // payouts:process (move money) implies payouts:view.
  PAYOUTS_VIEW: 'payouts:view',
  PAYOUTS_PROCESS: 'payouts:process'
}

const IMPLIES = {
  [CAP.PAYOUTS_PROCESS]: [CAP.PAYOUTS_VIEW]
}

// Preset roles bundle common capability sets. '*' = superadmin (everything).
// Role bundles start intentionally lean — capabilities that are still enforced
// via the legacy `checkUserAccess` tiers (e.g. FULL_TIME gating on the Events /
// Users lists) will move in here as those call sites migrate.
const ROLES = {
  superadmin: '*',
  full_time: [],
  support: []
}

// Per-person grants: roles + any explicit extra capabilities on top.
// Seeded to match today's behavior, plus the two worklist owners agreed with
// Josh: Layne owns the series worklist, Casey owns publishing.
//
// Deliberately granted to NOBODY (so superadmin-only for now):
//   bulk-email:send — kept to Josh until the tool is ready for wider use.
const GRANTS = {
  [JOSH]: {roles: ['superadmin']},
  [CASEY]: {roles: ['full_time'], caps: [CAP.EVENTS_EDIT, CAP.SPECIES_MANAGE, CAP.WORKLIST_PUBLISH, CAP.SPECIALISTS_MANAGE, CAP.EVENTS_CANCEL_DELETE, CAP.SERIES_IMPORT, CAP.PAYOUTS_PROCESS]},
  [LAYNE]: {roles: ['full_time'], caps: [CAP.WORKLIST_SERIES, CAP.SERIES_IMPORT]},
  [JASON]: {roles: ['full_time'], caps: [CAP.PAYOUTS_PROCESS]},
  [RUTH]: {roles: ['full_time'], caps: [CAP.PAYOUTS_PROCESS]},
  [RACHEL]: {roles: ['support']},
  [OLIVIA]: {roles: ['support']},
  [BRANDY]: {roles: ['support']},
  [SUSAN]: {roles: ['support']},
  [PEYTON]: {roles: ['support']},
  [KELSEY]: {roles: ['support']}
}

// Optional display owner per worklist capability — used for labels /
// notifications, NOT for gating (gating is the capability grant itself).
export const WORKLIST_OWNERS = {
  [CAP.WORKLIST_PUBLISH]: 'Casey',
  [CAP.WORKLIST_SERIES]: 'Layne',
  [CAP.PAYOUTS_VIEW]: 'Ruth'
}

function rolesFor (uid) {
  const grant = GRANTS[uid]
  return grant && grant.roles ? grant.roles : []
}

// Full set of capabilities a user holds (roles expanded + explicit caps).
// Returns null for superadmins (they implicitly hold everything).
export function capabilitiesFor (uid) {
  const grant = GRANTS[uid]
  if (!grant) return new Set()
  if ((grant.roles || []).includes('superadmin')) return null // all
  const caps = new Set(grant.caps || [])
  for (const role of grant.roles || []) {
    const def = ROLES[role]
    if (def && def !== '*') def.forEach(c => caps.add(c))
  }
  ;[...caps].forEach(c => (IMPLIES[c] || []).forEach(i => caps.add(i)))
  return caps
}

// The core check. Storage-agnostic: swapping GRANTS for a DB-loaded object
// later leaves every `can(...)` / `$can(...)` call site untouched.
export function can (uid, capability) {
  if (!uid || !capability) return false
  if (rolesFor(uid).includes('superadmin')) return true
  const caps = capabilitiesFor(uid)
  if (caps === null) return true
  return caps.has(capability)
}

export default {can, capabilitiesFor, CAP, WORKLIST_OWNERS}
